// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { HasLinksState } from "@insite/client-framework/Store/Links/LinksState";

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoryDepthLoaded as actualGetCategoryDepthLoaded,
    getCategoryState,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";

export const getPageLinkByUrl = (state: HasLinksState, url: string) => {
    return searchChildrenLinks(state.links.pageLinks[0], url);
};

const searchChildrenLinks: (pageLink: PageLinkModel, url: string) => PageLinkModel | undefined = (
    pageLink: PageLinkModel,
    url,
) => {
    if (pageLink && pageLink.url === url) {
        return pageLink;
    }
    if (pageLink && pageLink.children && pageLink.children.length) {
        for (let index = 0; index < pageLink.children.length; index++) {
            const child = pageLink.children[index];
            const result = searchChildrenLinks(child, url);
            if (result) {
                return result;
            }
        }
    }
};

export const getLink = (state: ApplicationState, { type, value }: LinkFieldValue) => {
    const link = type === "Category" ? getCategoryState(state, value) : null;
    switch (type) {
        case "Page":
            return getPageLinkByNodeId(state, value);
        case "Category":
            return {
                url: link?.value?.path, // TODO ISC-10781 make this work
                title: link?.value?.shortDescription,
            };
        case "Url":
            return {
                url: value,
                title: value,
            };
    }
};

export interface LinkModel {
    fields: {
        openInNewWindow: boolean;
        overriddenTitle: string;
        destination: LinkFieldValue;
    };
}

export function mapLinks<L extends LinkModel, R = {}>(
    state: ApplicationState,
    links?: L[],
    optionalReturnFunction?: (widgetLink: L, stateLink?: ReturnType<typeof getLink>) => R,
) {
    if (!links || links.length < 1) {
        return [];
    }
    return links.map(widgetLink => {
        const stateLink = getLink(state, widgetLink.fields.destination);
        const optionalReturnValues =
            typeof optionalReturnFunction === "function" ? optionalReturnFunction(widgetLink, stateLink) : {};
        return {
            url: stateLink?.url,
            title: stateLink?.title,
            ...optionalReturnValues,
        } as ReturnType<typeof getLink> & R;
    });
}
